import React, { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Stack, Typography } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import InsertEmoticonOutlinedIcon from '@mui/icons-material/InsertEmoticonOutlined';
import format from 'string-template';
import { StyledTextField } from '../../../../components/Inputs';
import EmojiPickerButton from '../../../../components/EmojiInput';
import { caretAfterShortcodes, replaceEmojiShortcodes } from '../../../../utils/emoji';
import {
  TOKENS,
  MAX_BODY_LEN,
  SMS_SEGMENT_LEN_GSM7,
  SMS_SEGMENT_LEN_UCS2,
} from '../utils/constants';
import {
  hasLinkToken,
  hasFirstNameToken,
  hasDuplicateTokens,
  substitutedLength as computeSubstitutedLength,
  substitutedSegments,
  insertAtCursor,
} from '../utils/composeBodyPreview';
import { ENCODING } from '../utils/smsEncoding';
import texts from '../texts.json';
import { TokenIndicator } from './style';

const copy = texts.sections.message;

const MessageBodyField = ({
  body,
  onBodyChange,
  minRows,
  coachName,
  hasImage,
}) => {
  const textareaRef = useRef(null);
  const linkPresent = hasLinkToken(body);
  const firstNamePresent = hasFirstNameToken(body);
  const sms = useMemo(() => substitutedSegments(body, { coachName }), [body, coachName]);
  const isUnicode = sms.encoding === ENCODING.UCS2;
  const singleSegmentLen = isUnicode ? SMS_SEGMENT_LEN_UCS2 : SMS_SEGMENT_LEN_GSM7;
  const counterMax = hasImage ? MAX_BODY_LEN : singleSegmentLen;
  const overLimit = sms.units > MAX_BODY_LEN;
  const willSplit = !hasImage && sms.units > singleSegmentLen;
  const showsUnicodeNotice = !hasImage && isUnicode;
  let counterColor = 'text.secondary';
  if (overLimit) counterColor = 'error';
  else if (willSplit) counterColor = 'warning.main';

  // Deferred a tick so it runs after React has committed the new value to the textarea.
  const moveCaret = (position) => {
    setTimeout(() => {
      if (!textareaRef.current) return;
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(position, position);
    }, 0);
  };

  const applyBodyChange = (raw) => {
    const next = replaceEmojiShortcodes(raw);
    if (hasDuplicateTokens(next)) return null;
    const shrinking = next.length < body.length;
    if (!shrinking && computeSubstitutedLength(next, { coachName }) > MAX_BODY_LEN) return null;
    onBodyChange(next);
    return next;
  };

  const handleTyping = (event) => {
    const raw = event.target.value;
    const rawCaret = event.target.selectionStart;
    const next = applyBodyChange(raw);
    if (next !== null && next !== raw) {
      moveCaret(caretAfterShortcodes(raw, rawCaret));
    }
  };

  const handleInsert = (text) => {
    const el = textareaRef.current;
    const start = el ? el.selectionStart : body.length;
    const end = el ? el.selectionEnd : body.length;
    const { body: nextBody, caret } = insertAtCursor(body, text, start, end);
    if (applyBodyChange(nextBody) !== null) moveCaret(caret);
  };

  const renderToken = (token, present, addedLabel, missingLabel) => {
    const label = present ? addedLabel : missingLabel;
    return (
      <TokenIndicator
        type="button"
        present={present}
        disabled={present}
        onClick={() => handleInsert(token)}
        title={label}
      >
        {present ? <CheckRoundedIcon sx={{ fontSize: 14 }} /> : null}
        {label}
      </TokenIndicator>
    );
  };

  const renderEmojiTrigger = ({ onClick }) => (
    <TokenIndicator type="button" onClick={onClick} title={copy.emojiLabel}>
      <InsertEmoticonOutlinedIcon sx={{ fontSize: 14 }} />
      {copy.emojiLabel}
    </TokenIndicator>
  );

  return (
    <>
      <StyledTextField
        multiline
        minRows={minRows}
        value={body}
        onChange={handleTyping}
        placeholder={copy.placeholder}
        inputRef={textareaRef}
      />
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        spacing={1.5}
        sx={{ mt: 1 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
          <Typography variant="caption" color="text.secondary">
            {copy.insertLabel}
          </Typography>
          {renderToken(TOKENS.FIRST_NAME, firstNamePresent, copy.firstNameAdded, copy.firstNameMissing)}
          {renderToken(TOKENS.LINK, linkPresent, copy.linkAdded, copy.linkMissing)}
          <EmojiPickerButton
            onChange={({ emoji }) => handleInsert(emoji)}
            renderTrigger={renderEmojiTrigger}
          />
        </Stack>
        <Typography
          variant="caption"
          color={counterColor}
          fontWeight={overLimit || willSplit ? 700 : 500}
        >
          {`${sms.units} / ${counterMax}`}
        </Typography>
      </Stack>
      {willSplit && (
        <Typography variant="caption" color="warning.main" fontWeight={600} sx={{ mt: 0.5, display: 'block' }}>
          {format(copy.splitWarning, { limit: singleSegmentLen, segments: sms.segments })}
        </Typography>
      )}
      {showsUnicodeNotice && (
        <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
          {copy.unicodeNotice}
        </Typography>
      )}
      {firstNamePresent && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {copy.firstNameSkipNote}
        </Typography>
      )}
    </>
  );
};

MessageBodyField.propTypes = {
  body: PropTypes.string.isRequired,
  onBodyChange: PropTypes.func.isRequired,
  minRows: PropTypes.number,
  coachName: PropTypes.string,
  hasImage: PropTypes.bool,
};

MessageBodyField.defaultProps = {
  minRows: 6,
  coachName: '',
  hasImage: false,
};

export default MessageBodyField;
